import { BRIEF_SYNTHESIS_TIMEOUT_MS, type FollowUpState, type FollowUpView } from '@sigora/domain';
import { useCallback, useEffect, useRef, useState } from 'react';

import { type BriefReadView, fetchFollowUps } from '../../api/client.ts';

/** How often the thread reads its follow-ups again while one is being answered or written. */
export const FOLLOW_UP_POLL_MS = 2_000;

/**
 * How long the thread waits on an answer or an update before it says so and stops reading. The
 * longest call either can make is a whole revision of the Brief, so this is that call's timeout
 * with room for the queue in front of it.
 */
export const FOLLOW_UP_WAIT_MS = BRIEF_SYNTHESIS_TIMEOUT_MS + 30_000;

/**
 * The states that change the Brief on the page: an outcome in its record, and for an update the
 * version it wrote. An answer waiting for its choice changes nothing there.
 */
const SETTLED: ReadonlySet<FollowUpState> = new Set(['finalized']);

/**
 * How long the window must have been away before its focus coming back reads the follow-ups again,
 * and how long one such read keeps another from being asked: a click from the composer to the page
 * and back is not a return.
 */
export const FOLLOW_UP_RETURN_AFTER_MS = 2_000;

/** The states the thread waits on. */
const IN_FLIGHT: ReadonlySet<FollowUpState> = new Set(['answering', 'updating']);

/**
 * What a follow-up says on screen. A read that says the same of every follow-up as the thread holds
 * changes nothing, so it is not a new list to draw. An answer never changes once stored.
 */
const shownAs = (followUp: FollowUpView): string =>
  JSON.stringify([
    followUp.id,
    followUp.state,
    followUp.retryable,
    followUp.answerReason,
    followUp.updateReason,
    followUp.updateRetryable,
    followUp.answer === null,
    followUp.outcome,
  ]);

const sameAsShown = (before: readonly FollowUpView[], after: readonly FollowUpView[]): boolean =>
  before.length === after.length &&
  before.every((followUp, index) => {
    const other = after[index];
    return other !== undefined && shownAs(followUp) === shownAs(other);
  });

/**
 * The study's list as a read gave it, with what writes answered since put in place of the read's
 * copy of each follow-up they name and after the rest: a write answered after the read was issued
 * says more about its follow-up than the read can.
 */
const withWrites = (
  read: readonly FollowUpView[],
  written: readonly FollowUpView[],
): FollowUpView[] => {
  const byId = new Map(written.map((followUp) => [followUp.id, followUp]));
  const listed = new Set(read.map((followUp) => followUp.id));

  return [
    ...read.map((followUp) => byId.get(followUp.id) ?? followUp),
    ...written.filter((followUp) => !listed.has(followUp.id)),
  ];
};

export interface FollowUps {
  /** The study's follow-ups, oldest first; `null` until the first read answers. */
  followUps: FollowUpView[] | null;
  /** Whether the first read failed. Asking still works. */
  loadFailed: boolean;
  /** Reads the follow-ups again. */
  reload: (signal?: AbortSignal) => Promise<void>;
  /**
   * Puts one follow-up as a write answered it in place of what the thread holds, or after it, with
   * the Brief the write was answered with when it finalized one.
   */
  adopt: (followUp: FollowUpView, read?: BriefReadView | null) => void;
  /**
   * Whether the wait on this follow-up, in the state it is in now, ran out before it settled. Each
   * follow-up's wait is its own, counted from when the thread first saw it in that state.
   */
  waitedOut: (followUp: FollowUpView) => boolean;
  /** Starts this follow-up's wait again, after Check again or Try again. */
  waitAgain: (followUpId: string) => void;
}

/**
 * The Brief thread's follow-ups: read on mount, read again every `FOLLOW_UP_POLL_MS` while one is
 * being answered or written and its wait has not run out, and replaced one at a time by what a
 * write answers. Each follow-up waits `FOLLOW_UP_WAIT_MS` from when the thread first saw it
 * answering or updating, so a question asked later neither extends an earlier one's wait nor runs
 * out with it.
 *
 * `onSettled` is told whenever a read or a write finalizes a follow-up — a note or a log line
 * chosen here or in another tab, or an update the worker wrote — so the Brief on the page, a
 * sibling tree, moves with it: given the Brief the write that finalized it was answered with, to
 * draw as it is, or `null` for a finalize a read found, to read the Brief again. A write this tab
 * made that finalized its follow-up tells it at once, whether or not a read has answered yet. Never for what the first read finds, which is
 * the thread arriving rather than anything changing, and never for an answer, which changes nothing
 * on the Brief.
 *
 * The follow-ups are read again whenever the tab comes back into view, which is how a choice made in
 * another tab reaches this one once nothing here is waited on.
 *
 * A read issued before a write answered is dropped when it lands, since it can only say less than
 * the write did — unless no read has answered yet: then the thread holds only what writes answered,
 * so the read is kept with those writes in place of its copies. Until a read has answered nothing
 * the thread holds is the study's list, so nothing a read then finds is taken for a follow-up
 * finalized since. A read that changes nothing on screen leaves the list as it was.
 */
export const useFollowUps = (
  studyId: string,
  onSettled: (read: BriefReadView | null) => void,
): FollowUps => {
  const [followUps, setFollowUps] = useState<FollowUpView[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const held = useRef<FollowUpView[] | null>(null);
  /** Whether a read has answered, which makes what the thread holds the study's list. */
  const listed = useRef(false);
  /** The follow-ups seen finalized, each of which tells `onSettled` at most once. */
  const told = useRef(new Set<string>());
  /** Whether the poll is running, which a return to the tab leaves to it. */
  const polling = useRef(false);
  /** Counts what has replaced the thread's follow-ups, so a read can tell it was overtaken. */
  const written = useRef(0);
  const alive = useRef(true);
  const settled = useRef(onSettled);
  settled.current = onSettled;
  /** How many times each follow-up's wait was started again, which makes its next wait a new one. */
  const [waitsAgain, setWaitsAgain] = useState<ReadonlyMap<string, number>>(() => new Map());
  /** When the thread first saw each wait, by its key. */
  const since = useRef(new Map<string, number>());
  /** The waits that ran out, by their key. */
  const [overdue, setOverdue] = useState<ReadonlySet<string>>(() => new Set());
  /** Moves once a poll has answered or a deadline has passed, so the wait is looked at again. */
  const [tick, setTick] = useState(0);

  useEffect(() => {
    alive.current = true;

    return () => {
      alive.current = false;
    };
  }, []);

  /**
   * Takes a new list; `wrote` is the follow-up a write of this tab's answered, if one did, and
   * `read` the Brief that write was answered with.
   */
  const take = useCallback(
    (
      next: FollowUpView[],
      wrote: FollowUpView | null = null,
      read: BriefReadView | null = null,
    ) => {
      const before = held.current;
      const wasListed = listed.current;
      written.current += 1;
      if (before !== null && sameAsShown(before, next)) {
        return;
      }

      held.current = next;
      setFollowUps(next);

      // Each follow-up finalized is told once, whichever of a read and this tab's write saw it first:
      // at once for this tab's own write, and for a read only once a read has made the list the
      // study's, so what the first read finds is the thread arriving rather than a change. A follow-up
      // the thread had not seen counts, since another tab may have asked and chosen it.
      const finalized = next.filter(
        (followUp) => SETTLED.has(followUp.state) && !told.current.has(followUp.id),
      );
      for (const followUp of finalized) {
        told.current.add(followUp.id);
      }
      const wroteIt = finalized.some((followUp) => followUp.id === wrote?.id);
      if (wroteIt || (finalized.length > 0 && before !== null && wasListed)) {
        settled.current(wroteIt ? read : null);
      }
    },
    [],
  );

  const reload = useCallback(
    async (signal?: AbortSignal) => {
      const issued = written.current;
      try {
        const read = await fetchFollowUps(studyId, signal);
        if (!alive.current || signal?.aborted === true) {
          return;
        }
        const overtaken = written.current !== issued;
        if (overtaken && listed.current) {
          return;
        }

        setLoadFailed(false);
        take(overtaken ? withWrites(read, held.current ?? []) : read);
        listed.current = true;
      } catch {
        if (alive.current && held.current === null) {
          setLoadFailed(true);
        }
      }
    },
    [studyId, take],
  );

  const adopt = useCallback(
    (followUp: FollowUpView, read: BriefReadView | null = null) => {
      const current = held.current ?? [];
      const at = current.findIndex((each) => each.id === followUp.id);
      take(
        at === -1
          ? [...current, followUp]
          : current.map((each, index) => (index === at ? followUp : each)),
        followUp,
        read,
      );
    },
    [take],
  );

  useEffect(() => {
    const controller = new AbortController();
    void reload(controller.signal);

    return () => {
      controller.abort();
    };
  }, [reload]);

  /**
   * A return to the tab reads the follow-ups again, so what another tab chose or asked shows here
   * rather than a choice offered until a press of it is refused. Polling stops once nothing is being
   * answered or written, and a choice can wait in an open tab for days. A return is the tab coming
   * back into view, or the window's focus coming back after it was away `FOLLOW_UP_RETURN_AFTER_MS`;
   * each asks at most once in that time, and none while the poll is reading anyway.
   */
  useEffect(() => {
    let blurredAt: number | null = null;
    let lastAsked = Number.NEGATIVE_INFINITY;
    let returning = false;
    const readAgain = () => {
      const now = Date.now();
      if (polling.current || returning || now - lastAsked < FOLLOW_UP_RETURN_AFTER_MS) {
        return;
      }
      lastAsked = now;
      returning = true;
      void reload().finally(() => {
        returning = false;
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') {
        readAgain();
      }
    };
    const onBlur = () => {
      blurredAt = Date.now();
    };
    const onFocus = () => {
      const away = blurredAt === null ? 0 : Date.now() - blurredAt;
      blurredAt = null;
      if (away >= FOLLOW_UP_RETURN_AFTER_MS && document.visibilityState === 'visible') {
        readAgain();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    globalThis.addEventListener('blur', onBlur);
    globalThis.addEventListener('focus', onFocus);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      globalThis.removeEventListener('blur', onBlur);
      globalThis.removeEventListener('focus', onFocus);
    };
  }, [reload]);

  /** A follow-up's wait in the state it is in: a new state, or a wait started again, is a new wait. */
  const waitKey = useCallback(
    (followUp: FollowUpView) =>
      `${followUp.id}:${followUp.state}#${String(waitsAgain.get(followUp.id) ?? 0)}`,
    [waitsAgain],
  );

  const waiting = (followUps ?? [])
    .filter((followUp) => IN_FLIGHT.has(followUp.state))
    .map(waitKey)
    .join(',');

  /**
   * One read at a time every `FOLLOW_UP_POLL_MS` while any wait is still running, and a timer for
   * the next wait to run out. A wait that has run out is marked and read for no longer; the poll
   * stops once every wait has.
   */
  useEffect(() => {
    if (waiting === '') {
      return undefined;
    }

    const now = Date.now();
    const keys = waiting.split(',');
    for (const key of keys) {
      if (!since.current.has(key)) {
        since.current.set(key, now);
      }
    }
    const endOf = (key: string) => (since.current.get(key) ?? now) + FOLLOW_UP_WAIT_MS;
    const ranOut = keys.filter((key) => endOf(key) <= now && !overdue.has(key));
    if (ranOut.length > 0) {
      setOverdue((current) => new Set([...current, ...ranOut]));
      return undefined;
    }

    const running = keys.filter((key) => endOf(key) > now);
    if (running.length === 0) {
      return undefined;
    }

    const controller = new AbortController();
    polling.current = true;
    const deadline = setTimeout(
      () => setTick((count) => count + 1),
      Math.min(...running.map(endOf)) - now,
    );
    const poll = setTimeout(() => {
      void reload(controller.signal).then(() => {
        if (alive.current && !controller.signal.aborted) {
          setTick((count) => count + 1);
        }
      });
    }, FOLLOW_UP_POLL_MS);

    return () => {
      polling.current = false;
      clearTimeout(deadline);
      clearTimeout(poll);
      controller.abort();
    };
  }, [waiting, overdue, tick, reload]);

  const waitedOut = useCallback(
    (followUp: FollowUpView) => IN_FLIGHT.has(followUp.state) && overdue.has(waitKey(followUp)),
    [overdue, waitKey],
  );

  const waitAgain = useCallback(
    (followUpId: string) =>
      setWaitsAgain((current) =>
        new Map(current).set(followUpId, (current.get(followUpId) ?? 0) + 1),
      ),
    [],
  );

  return { followUps, loadFailed, reload, adopt, waitedOut, waitAgain };
};
